import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  AppError,
  HealthItemType,
  MedicationSchedule,
  RecurrenceType,
  SyncSaveState,
} from '@/types'
import { repositories } from '@/services/repositories'
import { isAppError } from '@/lib/supabase/errors'
import { describeRecurrence, todayLocalDate } from '@/domain/recurrence'
import {
  Banner,
  Button,
  Card,
  Checkbox,
  ConfirmDestructiveAction,
  DateField,
  EmptyState,
  SegmentedControl,
  Select,
  Skeleton,
  Stepper,
  SyncStatus,
  TextArea,
  TextField,
  TimeField,
  useToast,
  WeekdaySelector,
} from '@/components/ui'
import { ClockIcon, TrashIcon } from '@/components/icons'
import { removeFuturePlannerItemsForSchedule, syncScheduleToPlanner } from '../plannerSync'
import { deviceTimezone, errorMessage } from '../saveState'
import {
  composeDosage,
  ruleFromForm,
  validateScheduleForm,
  DATE_PATTERN,
  type ScheduleFormState,
} from '../scheduleForm'

const TYPE_OPTIONS = [
  { value: 'supplement', label: 'Supplement' },
  { value: 'medication', label: 'Medicatie' },
  { value: 'other', label: 'Overig' },
]

const RECURRENCE_OPTIONS = [
  { value: 'once', label: 'Eenmalig' },
  { value: 'daily', label: 'Dagelijks' },
  { value: 'weekly', label: 'Wekelijks' },
  { value: 'monthly', label: 'Maandelijks' },
]

function initialForm(): ScheduleFormState {
  const today = todayLocalDate()
  return {
    name: '',
    healthType: 'supplement',
    dosage: '',
    unit: '',
    quantity: 1,
    startDate: today,
    hasEndDate: false,
    endDate: today,
    localTime: '08:00',
    timezone: deviceTimezone(),
    recurrenceType: 'daily',
    weekdays: [],
    dayOfMonth: Number(today.slice(8, 10)),
    instructions: '',
  }
}

type ScheduleBuilderProps = {
  userId: string
  onAnnounce: (message: string) => void
}

/**
 * Structured schedule builder. Saving creates the schedule server-first and
 * then materializes bounded planner items (today + 13 days) via plannerSync.
 */
export function ScheduleBuilder({ userId, onAnnounce }: ScheduleBuilderProps) {
  const { showToast } = useToast()
  const [form, setForm] = useState<ScheduleFormState>(initialForm)
  const [attempted, setAttempted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveState, setSaveState] = useState<SyncSaveState | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const [schedules, setSchedules] = useState<MedicationSchedule[] | null>(null)
  const [listError, setListError] = useState<AppError | null>(null)
  const [stopTarget, setStopTarget] = useState<MedicationSchedule | null>(null)
  const [stopping, setStopping] = useState(false)

  const patch = (changes: Partial<ScheduleFormState>) =>
    setForm((current) => ({ ...current, ...changes }))

  const loadSchedules = useCallback(async () => {
    try {
      const loaded = await repositories.medication.listSchedules()
      setSchedules(loaded.filter((schedule) => schedule.active))
      setListError(null)
    } catch (error) {
      setSchedules([])
      setListError(
        isAppError(error)
          ? error
          : {
              code: 'UNKNOWN',
              userMessage: errorMessage(error),
              retryable: true,
              source: 'database',
            },
      )
    }
  }, [])

  useEffect(() => {
    void loadSchedules()
  }, [loadSchedules])

  const rule = useMemo(() => ruleFromForm(form), [form])
  const errors = useMemo(() => validateScheduleForm(form), [form])
  const preview = useMemo(() => {
    if (!DATE_PATTERN.test(form.startDate)) return null
    return describeRecurrence(rule, form.startDate)
  }, [rule, form.startDate])

  const resetForm = (announce = true) => {
    setForm(initialForm())
    setAttempted(false)
    setActionError(null)
    if (announce) {
      onAnnounce('Het schemaformulier is leeggemaakt.')
    }
  }

  const handleSubmit = async () => {
    setAttempted(true)
    if (errors.length > 0) {
      onAnnounce(`Het formulier bevat ${errors.length} fout${errors.length === 1 ? '' : 'en'}.`)
      return
    }
    setSaving(true)
    setActionError(null)
    try {
      const schedule = await repositories.medication.createSchedule({
        clientId: userId,
        createdBy: userId,
        name: form.name.trim(),
        healthType: form.healthType,
        dosage: composeDosage(form),
        quantity: form.quantity,
        instructions: form.instructions.trim() || null,
        startDate: form.startDate,
        endDate: form.hasEndDate ? form.endDate : null,
        localTime: form.localTime,
        timezone: form.timezone.trim(),
        recurrenceType: form.recurrenceType,
        recurrenceRule: rule,
        active: true,
      })

      let plannerNote = ''
      try {
        const syncResult = await syncScheduleToPlanner(
          repositories.planner,
          schedule,
          todayLocalDate(),
        )
        plannerNote = ` ${syncResult.created} plannermoment${syncResult.created === 1 ? '' : 'en'} aangemaakt.`
      } catch (plannerError) {
        // The schedule itself is safely stored; only planner materialization failed.
        plannerNote = ' De Planner-items konden nu niet worden aangemaakt; probeer het later opnieuw.'
        showToast({ message: errorMessage(plannerError), tone: 'error' })
      }

      setSchedules((current) => [...(current ?? []), schedule])
      setSaveState('cloud_saved')
      showToast({ message: `${schedule.name} opgeslagen en aan Planner toegevoegd`, tone: 'success' })
      resetForm(false)
      onAnnounce(`Schema voor ${schedule.name} is opgeslagen.${plannerNote}`)
    } catch (error) {
      setSaveState('sync_failed')
      const message = errorMessage(error)
      setActionError(message)
      showToast({ message, tone: 'error' })
      onAnnounce(`Opslaan mislukt. ${message}`)
    } finally {
      setSaving(false)
    }
  }

  const handleStop = async () => {
    if (!stopTarget) return
    setStopping(true)
    setActionError(null)
    try {
      await repositories.medication.deactivateSchedule(stopTarget.id)
      // Past planner items stay as intake history; only future ones go away.
      try {
        await removeFuturePlannerItemsForSchedule(
          repositories.planner,
          stopTarget.id,
          todayLocalDate(),
        )
      } catch (plannerError) {
        showToast({
          message: `Schema gestopt, maar toekomstige planneritems konden niet worden verwijderd. ${errorMessage(plannerError)}`,
          tone: 'error',
        })
      }
      setSchedules((current) => (current ?? []).filter((s) => s.id !== stopTarget.id))
      setSaveState('cloud_saved')
      showToast({ message: `Schema voor ${stopTarget.name} gestopt`, tone: 'success' })
      onAnnounce(
        `Schema voor ${stopTarget.name} is gestopt. Toekomstige planneritems zijn verwijderd; eerdere dagen blijven bewaard.`,
      )
      setStopTarget(null)
    } catch (error) {
      setSaveState('sync_failed')
      const message = errorMessage(error)
      setActionError(message)
      showToast({ message, tone: 'error' })
    } finally {
      setStopping(false)
    }
  }

  return (
    <section className="health-section" aria-labelledby="schedule-builder-title">
      <h2 className="health-section-title" id="schedule-builder-title">
        <ClockIcon size={20} className="health-section-icon" />
        Nieuw schema
      </h2>
      <p className="health-hint">
        Plan vaste momenten voor supplementen of medicatie en zet ze direct in je Planner.
      </p>

      <Card padding="md" className="schedule-form">
        <TextField
          label="Naam"
          value={form.name}
          onChange={(v) => patch({ name: v })}
          required
          placeholder="Bijv. Magnesium"
          disabled={saving}
        />
        <Select
          label="Type"
          value={form.healthType}
          onChange={(v) => patch({ healthType: v as HealthItemType })}
          options={TYPE_OPTIONS}
        />
        <div className="schedule-form-row">
          <TextField
            label="Dosering"
            value={form.dosage}
            onChange={(v) => patch({ dosage: v })}
            placeholder="Bijv. 400"
            disabled={saving}
          />
          <TextField
            label="Eenheid of omschrijving"
            value={form.unit}
            onChange={(v) => patch({ unit: v })}
            placeholder="Bijv. mg, capsule, ml"
            disabled={saving}
          />
        </div>
        <Stepper
          label="Hoeveelheid per moment"
          value={form.quantity}
          onChange={(v) => patch({ quantity: v })}
          min={1}
          max={10}
          step={1}
        />
        <div className="schedule-form-row">
          <DateField
            label="Startdatum"
            value={form.startDate}
            onChange={(v) => patch({ startDate: v })}
          />
          <TimeField
            label="Tijdstip"
            value={form.localTime}
            onChange={(v) => patch({ localTime: v })}
          />
        </div>
        <Checkbox
          label="Met einddatum"
          checked={form.hasEndDate}
          onChange={(v) => patch({ hasEndDate: v })}
        />
        {form.hasEndDate ? (
          <DateField
            label="Einddatum"
            value={form.endDate}
            onChange={(v) => patch({ endDate: v })}
          />
        ) : null}
        <TextField
          label="Tijdzone"
          value={form.timezone}
          onChange={(v) => patch({ timezone: v })}
          hint="Standaard de tijdzone van je apparaat."
          disabled={saving}
        />
        <SegmentedControl
          label="Herhaling"
          value={form.recurrenceType}
          onChange={(v) => patch({ recurrenceType: v as RecurrenceType })}
          options={RECURRENCE_OPTIONS}
        />
        {form.recurrenceType === 'weekly' ? (
          <WeekdaySelector value={form.weekdays} onChange={(v) => patch({ weekdays: v })} />
        ) : null}
        {form.recurrenceType === 'monthly' ? (
          <Stepper
            label="Dag van de maand"
            value={form.dayOfMonth}
            onChange={(v) => patch({ dayOfMonth: v })}
            min={1}
            max={31}
            step={1}
          />
        ) : null}
        <TextArea
          label="Instructies (optioneel)"
          value={form.instructions}
          onChange={(v) => patch({ instructions: v })}
          rows={2}
          placeholder="Bijv. innemen met water"
        />

        {preview ? (
          <p className="schedule-preview" aria-live="polite">
            <span className="schedule-preview-label">Voorbeeld</span>
            {preview}
          </p>
        ) : null}

        {attempted && errors.length > 0 ? (
          <Banner tone="error">
            <p className="health-library-item-name">Controleer het formulier</p>
            <ul className="validation-list">
              {errors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Banner>
        ) : null}
        {actionError ? <Banner tone="error">{actionError}</Banner> : null}

        <div className="schedule-actions">
          <Button
            variant="primary"
            fullWidth
            loading={saving}
            disabled={saving}
            onClick={() => void handleSubmit()}
          >
            Voeg toe aan Planner
          </Button>
          <Button variant="ghost" fullWidth disabled={saving} onClick={() => resetForm()}>
            Opnieuw beginnen
          </Button>
        </div>
        <SyncStatus state={saveState} />
      </Card>

      {schedules === null ? (
        <Skeleton lines={2} />
      ) : listError ? (
        <Banner tone="error">
          {listError.userMessage}{' '}
          <Button variant="ghost" size="sm" onClick={() => void loadSchedules()}>
            Opnieuw proberen
          </Button>
        </Banner>
      ) : schedules.length === 0 ? (
        <EmptyState
          title="Nog geen schema's"
          body="Schema's die je hier maakt verschijnen in je Planner voor de komende 14 dagen."
        />
      ) : (
        <ul className="health-library-list" aria-label="Actieve schema's">
          {schedules.map((schedule) => (
            <li key={schedule.id}>
              <Card padding="sm">
                <div className="health-library-item">
                  <div className="health-library-item-main">
                    <p className="health-library-item-name">
                      {schedule.name}
                      {schedule.dosage ? ` · ${schedule.dosage}` : ''}
                    </p>
                    <p className="health-library-item-meta">
                      {describeRecurrence(schedule.recurrenceRule, schedule.startDate)} ·{' '}
                      {schedule.localTime}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={stopping}
                    onClick={() => setStopTarget(schedule)}
                  >
                    <TrashIcon size={16} /> Stop
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDestructiveAction
        open={stopTarget !== null}
        title={stopTarget ? `Schema voor ${stopTarget.name} stoppen?` : 'Schema stoppen?'}
        body="Toekomstige planneritems worden verwijderd. Eerdere dagen en ingenomen momenten blijven bewaard."
        confirmLabel="Stop schema"
        onConfirm={() => void handleStop()}
        onCancel={() => setStopTarget(null)}
      />
    </section>
  )
}
